import type {
  EmcProductComplianceFileOutputDocument,
  EmcProductComplianceFilesState,
} from '@/shared/hooks/api/emcComplianceFiles'
import type { EmcProductComplianceFileEvidenceSlot } from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { findFirstIncompleteRequiredStep } from '@/shared/briefing'
import {
  hasConfirmedHarmonisedStandards,
  type ConfirmedHarmonisedStandard,
} from '@/v2/lib/confirmedHarmonisedStandards'

export { findFirstIncompleteRequiredStep }

export type EmcBriefingStepKey =
  | 'applicability'
  | 'productDescription'
  | 'productIdentification'
  | 'manufacturerInformation'
  | 'confirmedStandards'
  | 'productDrawings'
  | 'testReportElectromagneticDisturbance'
  | 'testReportElectromagneticImmunity'
  | 'euDeclaration'
  | 'productSignOff'

export type EmcBriefingStepStatus = 'done' | 'open' | 'partial' | 'guidance'

export type EmcBriefingStepRequiredness =
  | 'required'
  | 'optional'
  | 'informational'

export type EmcBriefingStep = {
  key: EmcBriefingStepKey
  status: EmcBriefingStepStatus
  requiredness: EmcBriefingStepRequiredness
}

export type EmcLegislationBriefing = {
  steps: EmcBriefingStep[]
}

export type { ConfirmedHarmonisedStandard }

export function parseConfirmedEmcStandards(
  json: unknown | null
): ConfirmedHarmonisedStandard[] {
  if (!Array.isArray(json)) {
    return []
  }

  const parsed: ConfirmedHarmonisedStandard[] = []
  for (const entry of json) {
    if (typeof entry !== 'object' || entry == null) {
      continue
    }
    const designation =
      'designation' in entry && typeof entry.designation === 'string'
        ? entry.designation.trim()
        : ''
    if (designation.length === 0) {
      continue
    }
    const title =
      'title' in entry && typeof entry.title === 'string'
        ? entry.title.trim()
        : undefined
    parsed.push({
      designation,
      ...(title != null && title.length > 0 ? { title } : {}),
    })
  }
  return parsed
}

function findLatestCompletedOutputDocument(
  outputDocuments: EmcProductComplianceFileOutputDocument[]
): EmcProductComplianceFileOutputDocument | null {
  return (
    outputDocuments.find((document) => document.status === 'COMPLETED') ?? null
  )
}

export function emcBriefingStepRequiredness(
  key: EmcBriefingStepKey
): EmcBriefingStepRequiredness {
  if (key === 'applicability' || key === 'euDeclaration') {
    return 'informational'
  }
  return 'required'
}

function buildStep(
  key: EmcBriefingStepKey,
  status: EmcBriefingStepStatus
): EmcBriefingStep {
  return {
    key,
    status,
    requiredness: emcBriefingStepRequiredness(key),
  }
}

function slotFilledStatus(hasFiles: boolean): EmcBriefingStepStatus {
  if (hasFiles) {
    return 'done'
  }
  return 'open'
}

function isEmcTechnicalDrawingsStepFilled(
  slotFilled: Record<EmcProductComplianceFileEvidenceSlot, boolean>
): boolean {
  return (
    slotFilled.DRAWINGS_SCHEMAS ||
    slotFilled.DESCRIPTIONS_AND_EXPLANATIONS
  )
}

export function buildEmcLegislationBriefing(input: {
  hasProductDescription: boolean
  hasProductIdentification: boolean
  hasManufacturerInformation: boolean
  slotFilled: Record<EmcProductComplianceFileEvidenceSlot, boolean>
  files: EmcProductComplianceFilesState
}): EmcLegislationBriefing {
  const hasEuDeclaration =
    findLatestCompletedOutputDocument(input.files.outputDocuments) != null

  let productDescriptionStatus: EmcBriefingStepStatus = 'open'
  if (input.hasProductDescription) {
    productDescriptionStatus = 'done'
  }

  let productIdentificationStatus: EmcBriefingStepStatus = 'open'
  if (input.hasProductIdentification) {
    productIdentificationStatus = 'done'
  }

  let manufacturerInformationStatus: EmcBriefingStepStatus = 'open'
  if (input.hasManufacturerInformation) {
    manufacturerInformationStatus = 'done'
  }

  let confirmedStandardsStatus: EmcBriefingStepStatus = 'open'
  if (
    hasConfirmedHarmonisedStandards(
      input.files.product?.confirmedStandardsJson ?? null
    )
  ) {
    confirmedStandardsStatus = 'done'
  }

  let euDeclarationStatus: EmcBriefingStepStatus = 'open'
  if (hasEuDeclaration) {
    euDeclarationStatus = 'done'
  }

  let productSignOffStatus: EmcBriefingStepStatus = 'open'
  if (input.files.product?.completedAt != null) {
    productSignOffStatus = 'done'
  }

  return {
    steps: [
      buildStep('applicability', 'done'),
      buildStep('productDescription', productDescriptionStatus),
      buildStep('productIdentification', productIdentificationStatus),
      buildStep('manufacturerInformation', manufacturerInformationStatus),
      buildStep('confirmedStandards', confirmedStandardsStatus),
      buildStep(
        'productDrawings',
        slotFilledStatus(isEmcTechnicalDrawingsStepFilled(input.slotFilled))
      ),
      buildStep(
        'testReportElectromagneticDisturbance',
        slotFilledStatus(input.slotFilled.TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE)
      ),
      buildStep(
        'testReportElectromagneticImmunity',
        slotFilledStatus(input.slotFilled.TEST_REPORT_ELECTROMAGNETIC_IMMUNITY)
      ),
      buildStep('euDeclaration', euDeclarationStatus),
      buildStep('productSignOff', productSignOffStatus),
    ],
  }
}
